import {
  GridRowModes,
  GridRowModesModel,
  GridToolbarContainer,
} from "@mui/x-data-grid";
import ActionButtons from "../ActionUserButtons";
import { API_URL, handleSendMessage, syncItems } from "src/utilities";
import { useMutation } from "@tanstack/react-query";
import AddIcon from "@mui/icons-material/Add";

import { useAuth } from "src/context/auth";
import { useNotifications } from "@toolpad/core/useNotifications";
import { Box, Button, Stack } from "@mui/material";
import { IBankAccount } from "src/interface/IBankAccount";
import { IAppUser } from "src/interface/IUser";

interface EditToolbarBankAccountProps {
  userRef?: string;
  setRows: (newRows: (oldRows: IBankAccount[]) => IBankAccount[]) => void;
  setRowModesModel: (
    newModel: (oldModel: GridRowModesModel) => GridRowModesModel
  ) => void;
  onClose: () => void;
  rows: IBankAccount[];
  updatingBankAccount?: IBankAccount[];
  setUpdatingBankAccount?: (updatingBankAccount: IBankAccount[]) => void;
  existingBankAccount?: IBankAccount[];
  refetchUser: () => void;
}

export function EditToolbarBankAccount({
  rows,
  setRowModesModel,
  setRows,
  onClose,
  setUpdatingBankAccount,
  existingBankAccount,
  updatingBankAccount,
  userRef,
  refetchUser,
}: EditToolbarBankAccountProps) {
  const { user } = useAuth();
  const handleClick = () => {
    const userID = rows ? rows.length + 1 : 1; // ایجاد یک شناسه منحصر به فرد برای ردیف جدید
    const newRow: IBankAccount = {
      id: userID,
      bankName: "",
      cardNumber: "",
      desc: "",
      mobile: "",
      ownerName: "",
      userId: "",
      cvV2: "",
      expireDate: "",
      nationalCode: "",
      isNew: true, // نشان‌دهنده ردیف جدید
    };

    setRows((oldRows) => [...(oldRows || ([] as IBankAccount[])), newRow]);

    setRowModesModel((oldModel) => ({
      ...oldModel,
      [userID]: { mode: GridRowModes.Edit, fieldToFocus: "itemRef" },
    }));
  };
  const notifications = useNotifications();

  const { mutateAsync: insertBankAccAsync, isPending: bankAccLoading } =
    useMutation({
      mutationFn: async (body: any) => {
        const response = await fetch(API_URL + "bank-accounts", {
          method: "POST",
          headers: {
            Accept: "*/*",
            "Content-Type": "application/json-patch+json",
            Authorization: `Bearer ${user?.token}`,
          },
          body: JSON.stringify(body),
        });
        return await response.json();
      },
      onSuccess: (responseData) => {
        handleSendMessage(
          notifications,
          "حساب بانکی جدید اضافه شد",
          "success",
          3000,
          "insert"
        );
        JSON.stringify(responseData);
        refetchUser();
      },
      onError: (error) => {
        handleSendMessage(
          notifications,
          "خطا در عملیات افزودن حساب های بانکی!",
          "error",
          3000,
          "error"
        );
        console.log(error);
      },
    });
  const { mutateAsync: deleteBankAccAsync, isPending: isDeleteBankAccLoading } =
    useMutation({
      mutationFn: async (body: any) => {
        const response = await fetch(API_URL + "bank-accounts/" + body.id, {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${user?.token}`,
            Aceept: "application/json",
          },
        });

        return await response.json();
      },
      onSuccess: (responseData) => {
        handleSendMessage(
          notifications,
          "حساب بانکی حذف شد",
          "success",
          3000,
          "delete"
        );

        JSON.stringify(responseData);
        refetchUser();
      },
      onError: (error) => {
        handleSendMessage(
          notifications,
          "خطا در عملیات حذف حساب های بانکی!",
          "error",
          3000,
          "error"
        );

        console.log(error);
      },
    });

  const { mutateAsync: updateBankAsync, isPending: isPendingBankEdit } =
    useMutation({
      mutationFn: async (body: IBankAccount) => {
        const response = await fetch(API_URL + "bank-accounts/" + body.id, {
          method: "Put",
          headers: {
            Accept: "*/*",
            "Content-Type": "application/json-patch+json",
            Authorization: `Bearer ${user?.token}`,
          },
          body: JSON.stringify(body),
        });

        return await response.json();
      },
      onSuccess: (responseData) => {
        handleSendMessage(
          notifications,
          "حساب بانکی بروزرسانی شد",
          "success",
          3000,
          "update"
        );

        JSON.stringify(responseData);
        refetchUser();
      },
      onError: (error) => {
        handleSendMessage(
          notifications,
          "خطا در عملیات همگام‌سازی حساب های بانکی!",
          "error",
          3000,
          "error"
        );
        console.log(error);
      },
    });

  const onSave = async () => {
    if (
      (existingBankAccount?.length === 0 || !existingBankAccount) &&
      updatingBankAccount?.length === 0
    ) {
      handleSendMessage(
        notifications,
        "هیچ حساب بانکی برای ذخیره سازی وجود ندارد",
        "error",
        3000,
        "error"
      );
      return;
    }
    try {
      await syncItems<IBankAccount>({
        deleteItemAsync: deleteBankAccAsync,
        existingItems: existingBankAccount,
        insertItemAsync: insertBankAccAsync,
        isEdit: true,
        RefField: "userId",
        RefValue: userRef,
        updatedItems: updatingBankAccount,
        updateItemAsync: updateBankAsync,
      });
    } catch (error) {
      console.error("خطا در همگام‌سازی کلی:", error);
      handleSendMessage(
        notifications,
        "خطا در عملیات همگام‌سازی حساب های بانکی!",
        "error",
        3000,
        "error"
      );
    }
  };
  const onSaveAndClose = async () => {
    await onSave(); // منتظر تکمیل onSave
    setUpdatingBankAccount && setUpdatingBankAccount([]);
    onClose(); //
  };
  return (
    <GridToolbarContainer>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        width="100%"
        sx={{ padding: 1 }}
      >
        <Box>
          <Button color="primary" startIcon={<AddIcon />} onClick={handleClick}>
            حساب جدید
          </Button>
        </Box>
        <ActionButtons
          onSave={onSave}
          onSaveAndClose={onSaveAndClose}
          isLoading={
            isPendingBankEdit || isDeleteBankAccLoading || bankAccLoading
          }
        />
      </Stack>
    </GridToolbarContainer>
  );
}
